import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Box, Drawer, List, ListItem, ListItemButton, ListItemText, Typography, Button, Grid, Card, CardContent } from '@mui/material';
import { getDashboardStats } from '../services/api';
import Logo from '../../content/CRPLOGO-removebg.png'; 
import ClientManagement from './ClientManagement';
import ProjectTracker from './ProjectTracker';
import Payments from './Payments';
import Scheduler from './Scheduler';
import Notifications from './Notifications';
import ChatMonitor from './ChatMonitor';
import Reports from './Reports';
import ServiceConfig from './ServiceConfig';
import UserManagement from './UserManagement';

const DrawerStyled = styled(Drawer)(({ theme }) => ({
  width: 240,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: 240,
    background: 'linear-gradient(180deg, #0a0a0a, #1a1a1a)',
    borderRight: '1px solid rgba(255, 255, 255, 0.08)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.6)',
    padding: '30px 0',
  },
}));

const ContentStyled = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: '30px',
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.04), rgba(0, 0, 0, 0.9))',
  borderRadius: '16px',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.5)',
}));

function Dashboard() {
  const [activeSection, setActiveSection] = useState('overview');
  const [stats, setStats] = useState({ clients: 0, projects: 0, payments: 0, events: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getDashboardStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };
    fetchStats();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    window.location.reload();
  };

  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'clients', label: 'Clients' },
    { id: 'projects', label: 'Projects' },
    { id: 'payments', label: 'Payments' },
    { id: 'scheduler', label: 'Scheduler' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'chat', label: 'Chat Monitor' },
    { id: 'reports', label: 'Reports' },
    { id: 'services', label: 'Service Config' },
    { id: 'users', label: 'User Management' },
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <DrawerStyled variant="permanent">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <motion.img
            src={Logo}
            alt="Company Logo"
            style={{ width: '150px', marginBottom: '20px' }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          />
        </Box>
        <List>
          {sections.map((section) => (
            <ListItem key={section.id} disablePadding>
              <ListItemButton
                onClick={() => setActiveSection(section.id)}
                sx={{
                  py: 2,
                  color: '#e0e0e0', // Default light gray text
                  '&:hover': {
                    background: 'linear-gradient(90deg, #ffffff, #e0e0e0)', // White-to-gray gradient
                    color: '#0a0a0a', // Black text on hover
                  },
                }}
              >
                <ListItemText primary={section.label} />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                py: 2,
                mt: 'auto',
                background: 'rgba(255, 85, 85, 0.2)',
                color: '#ff9999',
                '&:hover': {
                  background: '#ff5555',
                  color: '#ffffff', // White text on hover, not black
                },
              }}
            >
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </DrawerStyled>
      <ContentStyled>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {activeSection === 'overview' && (
            <Box>
              <Typography variant="h1" gutterBottom>Admin Overview</Typography>
              <Grid container spacing={4}>
                {[
                  { title: 'Clients', value: stats.clients },
                  { title: 'Projects', value: stats.projects },
                  { title: 'Payments', value: `$${stats.payments.toLocaleString()}` },
                  { title: 'Events', value: stats.events },
                ].map((stat) => (
                  <Grid item xs={12} sm={6} md={3} key={stat.title}>
                    <Card>
                      <CardContent>
                        <Typography variant="h3">{stat.title}</Typography>
                        <Typography variant="body1" sx={{ fontSize: '2rem', mt: 1 }}>{stat.value}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 4, display: 'flex', gap: 3, justifyContent: 'center' }}>
                <Button variant="contained" onClick={() => setActiveSection('clients')}>Manage Clients</Button>
                <Button variant="contained" onClick={() => setActiveSection('projects')}>Add Project</Button>
                <Button variant="contained" onClick={() => setActiveSection('notifications')}>Send Notification</Button>
              </Box>
            </Box>
          )}
          {activeSection === 'clients' && <ClientManagement />}
          {activeSection === 'projects' && <ProjectTracker />}
          {activeSection === 'payments' && <Payments />}
          {activeSection === 'scheduler' && <Scheduler />}
          {activeSection === 'notifications' && <Notifications />}
          {activeSection === 'chat' && <ChatMonitor />}
          {activeSection === 'reports' && <Reports />}
          {activeSection === 'services' && <ServiceConfig />}
          {activeSection === 'users' && <UserManagement />}
        </motion.div>
      </ContentStyled>
    </Box>
  );
}

export default Dashboard;